"use client";

import {
  Badge,
  Box,
  Divider,
  Flex,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Text,
} from "@chakra-ui/react";
import { usePathname, useRouter } from "next/navigation";
import { useCallback, useMemo, useState } from "react";
import { HiOutlineBell } from "react-icons/hi2";
import { NotificationBellDropdownSkeleton } from "@/components/ui/NotificationBellDropdownSkeleton";
import { TouchAwareTooltip } from "@/components/ui/TouchAwareTooltip";
import { resolveNotificationMakeupLink } from "@/features/makeups/shared/resolveNotificationMakeupLink";
import { NOTIFICATIONS_BELL_UNREAD_PAGE_SIZE } from "@/features/notifications/constants";
import {
  useCachedInboxNotifications,
  useMarkedReadNotificationIds,
  useNotificationsMarkAsRead,
  useNotificationsUnreadCountGet,
  useNotificationsUnreadGet,
} from "@/features/notifications/hooks";
import { normalizeNotificationId } from "@/features/notifications/utils/notification-read-state";
import {
  getBellUnreadCount,
  mergeBellUnreadNotifications,
  resolveNotificationAudience,
} from "@/features/notifications/utils/notification-visibility";
import { useAuth } from "@/lib/contexts";
import { useThemeTokens } from "@/lib/theme/tokens";
import { PageTypeAdmin, PageTypePortal, UserRoles } from "@/lib/types";
import type { GetNotificationResponse } from "@/openapi/generated";

const BELL_PREVIEW_PAGE_SIZE = 5;

interface NotificationButtonProps {
  /**
   * Size of the button
   */
  size?: "sm" | "md" | "lg";
}

const sizeMap = {
  sm: { w: "32px", h: "32px", iconSize: "16px" },
  md: { w: "40px", h: "40px", iconSize: "20px" },
  lg: { w: "48px", h: "48px", iconSize: "24px" },
};

export const NotificationButton = ({
  size = "sm",
}: NotificationButtonProps) => {
  const theme = useThemeTokens();
  const router = useRouter();
  const pathname = usePathname();
  const user = useAuth();
  const userId = user?.id;
  const activeRole = resolveNotificationAudience(pathname, user);

  const sizeStyles = sizeMap[size];

  const { count: apiUnreadCount, refetch: refetchUnreadCount } =
    useNotificationsUnreadCountGet({
      userId: userId ?? 0,
    });

  const {
    data: notificationsData,
    isPending: isNotificationsPending,
    isFetching: isNotificationsFetching,
    refetch: refetchUnreadNotifications,
  } = useNotificationsUnreadGet({
    userId: userId ?? 0,
    page: 1,
    pageSize: NOTIFICATIONS_BELL_UNREAD_PAGE_SIZE,
    // Count polls for the badge; list stays idle until the menu opens.
    refetchInterval: false,
  });

  const { data: markedReadIds = [] } = useMarkedReadNotificationIds(
    userId ?? 0,
  );
  const trustedInboxItems = useCachedInboxNotifications(userId ?? 0);

  const markAsReadMutation = useNotificationsMarkAsRead({
    userId,
  });

  const markedReadIdSet = useMemo(
    () => new Set(markedReadIds.map(normalizeNotificationId)),
    [markedReadIds],
  );

  const unreadCount = useMemo(
    () =>
      getBellUnreadCount({
        role: activeRole,
        apiUnreadCount,
        unreadItems: notificationsData?.items ?? [],
        unreadTotalCount:
          notificationsData?.totalCount == null
            ? null
            : Number(notificationsData.totalCount),
        hasUnreadListData: notificationsData != null,
        markedReadIdSet,
        trustedItems: trustedInboxItems,
      }),
    [
      activeRole,
      apiUnreadCount,
      markedReadIdSet,
      notificationsData,
      trustedInboxItems,
    ],
  );

  const allUnreadNotifications = useMemo(
    () =>
      mergeBellUnreadNotifications(
        notificationsData?.items ?? [],
        trustedInboxItems,
        activeRole,
        markedReadIdSet,
      ),
    [activeRole, markedReadIdSet, notificationsData?.items, trustedInboxItems],
  );

  const unreadNotifications = useMemo(() => {
    return allUnreadNotifications.slice(0, BELL_PREVIEW_PAGE_SIZE);
  }, [allUnreadNotifications]);

  const additionalUnreadCount = Math.max(
    0,
    unreadCount - unreadNotifications.length,
  );

  const notificationsPage = pathname.startsWith("/admin")
    ? PageTypeAdmin.Notifications
    : PageTypePortal.Notifications;

  const formatTimeAgo = (date: Date): string => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const showDropdownSkeleton =
    isMenuOpen &&
    unreadNotifications.length === 0 &&
    (isNotificationsPending || (isNotificationsFetching && unreadCount > 0));

  const isDropdownRefreshing =
    isMenuOpen &&
    isNotificationsFetching &&
    !showDropdownSkeleton &&
    unreadNotifications.length > 0;

  const handleMenuOpen = useCallback(() => {
    setIsMenuOpen(true);
    if (!userId) return;
    // Keep count/list in sync without background-polling the heavier unread list.
    void refetchUnreadCount();
    void refetchUnreadNotifications();
  }, [refetchUnreadCount, refetchUnreadNotifications, userId]);

  const scrollAreaSx = {
    "&::-webkit-scrollbar": {
      width: "6px",
    },
    "&::-webkit-scrollbar-track": {
      bg: theme.scrollbar.track,
      borderRadius: "4px",
    },
    "&::-webkit-scrollbar-thumb": {
      bg: theme.scrollbar.thumb,
      borderRadius: "4px",
      "&:hover": {
        bg: theme.scrollbar.thumbHover,
      },
    },
  };

  const handleShowAllNotifications = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsMenuOpen(false);
    router.push(notificationsPage);
  };

  const dismissNotification = useCallback(
    (notificationId: string) => {
      if (!userId || markAsReadMutation.isPending) return;

      setExpandedId((current) => (current === notificationId ? null : current));
      markAsReadMutation.mutate([String(notificationId)]);
    },
    [markAsReadMutation, userId],
  );

  const handleNotificationClick = useCallback(
    (notification: GetNotificationResponse, e: React.MouseEvent) => {
      e.stopPropagation();
      const preview = notification.message?.trim() ?? "";
      const isLongMessage = preview.length > 120;

      if (isLongMessage && expandedId !== notification.id) {
        setExpandedId(notification.id);
        return;
      }

      const makeupLink =
        activeRole === UserRoles.Member
          ? resolveNotificationMakeupLink(notification)
          : null;
      dismissNotification(notification.id);
      if (makeupLink) {
        router.push(makeupLink);
      }
    },
    [activeRole, dismissNotification, expandedId, router],
  );

  const renderNotificationItem = (notification: GetNotificationResponse) => {
    const createdAt = notification.createdAt
      ? new Date(notification.createdAt)
      : new Date();
    const isExpanded = expandedId === notification.id;
    const preview = notification.message?.trim() ?? "";
    const isLongMessage = preview.length > 120;
    const isUnread = !markedReadIdSet.has(
      normalizeNotificationId(notification.id),
    );

    return (
      <MenuItem
        key={notification.id}
        data-testid="notification-bell-item"
        aria-label={
          isExpanded
            ? "Mark notification as read and dismiss"
            : "Expand notification"
        }
        color={theme.text.primary}
        bg={isExpanded ? theme.accent.primaryBg : "transparent"}
        borderRadius="6px"
        w="calc(100% - 10px)"
        mx="5px"
        py={2}
        px={3}
        fontSize="sm"
        fontWeight="500"
        transition="all 0.15s ease-out"
        whiteSpace="normal"
        sx={{
          "@media (hover: hover)": {
            "&:hover": {
              bg: theme.accent.primaryBg,
              color: theme.accent.primary,
            },
          },
        }}
        onClick={(e) => handleNotificationClick(notification, e)}
      >
        <Flex direction="column" gap={1} w="100%">
          <Flex justify="space-between" align="flex-start" gap={2}>
            <Text
              fontSize="sm"
              fontWeight="600"
              color={theme.text.primary}
              flex={1}
            >
              {notification.title || "Notification"}
            </Text>
            {isUnread && (
              <Box
                w="8px"
                h="8px"
                borderRadius="full"
                bg={theme.accent.primary}
                flexShrink={0}
                mt={1}
              />
            )}
          </Flex>
          <Text
            fontSize="xs"
            color={theme.text.secondary}
            noOfLines={isExpanded ? undefined : 2}
            lineHeight="1.5"
            whiteSpace="pre-wrap"
          >
            {preview}
          </Text>
          <Flex justify="space-between" align="center" gap={2} mt={0.5}>
            <Text fontSize="xs" color={theme.text.tertiary}>
              {formatTimeAgo(createdAt)}
            </Text>
            <Text
              fontSize="xs"
              fontWeight="500"
              color={theme.accent.muted}
              flexShrink={0}
            >
              {isLongMessage
                ? isExpanded
                  ? "Tap again to dismiss"
                  : "Tap to read"
                : "Tap to dismiss"}
            </Text>
          </Flex>
        </Flex>
      </MenuItem>
    );
  };

  return (
    <Menu
      placement="bottom-end"
      closeOnSelect={false}
      isOpen={isMenuOpen}
      onOpen={handleMenuOpen}
      onClose={() => {
        setIsMenuOpen(false);
        setExpandedId(null);
      }}
    >
      <TouchAwareTooltip
        label="Notifications"
        placement="bottom"
        isDisabled={isMenuOpen}
      >
        <MenuButton
          as={Box}
          data-testid="notification-bell-button"
          position="relative"
          w={sizeStyles.w}
          h={sizeStyles.h}
          display="flex"
          alignItems="center"
          justifyContent="center"
          borderWidth="1px"
          borderColor={theme.borderGlass.default}
          color={theme.text.secondary}
          borderRadius="8px"
          transition="all 0.18s ease-out"
          cursor="pointer"
          sx={{
            "@media (hover: hover)": {
              "&:hover": {
                bg: theme.surfaceGlass.cardStrong,
                borderColor: theme.accent.primaryBorder,
                color: theme.accent.primary,
              },
            },
            "@media (hover: none) and (pointer: coarse)": {
              "&:active": {
                transform: "scale(0.95)",
              },
            },
          }}
          _focus={{
            boxShadow: theme.focusRing,
            outline: "none",
          }}
        >
          <Box
            position="relative"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <HiOutlineBell size={sizeStyles.iconSize} />
            {unreadCount > 0 && (
              <Badge
                data-testid="notification-bell-badge"
                position="absolute"
                top="-6px"
                right="-6px"
                bg={theme.accent.primary}
                color="white"
                fontSize="9px"
                fontWeight="700"
                borderRadius="full"
                minW="16px"
                h="16px"
                display="flex"
                alignItems="center"
                justifyContent="center"
                px={unreadCount > 9 ? "4px" : "0"}
                boxShadow="0 2px 6px rgba(0,0,0,0.25)"
                borderWidth="2px"
                borderColor={theme.surfaceGlass.card}
                zIndex={1}
              >
                {unreadCount > 9 ? "9+" : unreadCount}
              </Badge>
            )}
          </Box>
        </MenuButton>
      </TouchAwareTooltip>
      <Portal>
        <MenuList
          w={{ base: "calc(100vw - 2rem)", md: "400px" }}
          maxH="500px"
          overflow="hidden"
          display="flex"
          flexDirection="column"
          zIndex={2000}
          bg={"none"}
          backdropFilter="blur(14px)"
          borderWidth="1px"
          borderColor={theme.borderGlass.default}
          borderRadius="8px"
          boxShadow={theme.shadow.card}
          py={1}
          marginTop={{ base: "0.5rem", md: "auto" }}
          marginLeft={{ base: "1rem", md: "auto" }}
        >
          <Box
            flex="1"
            minH={0}
            overflowY="auto"
            py={1}
            sx={scrollAreaSx}
            opacity={isDropdownRefreshing ? 0.65 : 1}
            transition="opacity 0.15s ease"
            aria-busy={showDropdownSkeleton || isDropdownRefreshing}
          >
            {showDropdownSkeleton ? (
              <NotificationBellDropdownSkeleton />
            ) : unreadNotifications.length === 0 ? (
              <Box px={4} py={6} textAlign="center">
                <Text fontSize="sm" color={theme.text.tertiary}>
                  No unread notifications
                </Text>
              </Box>
            ) : (
              <>
                {unreadNotifications.map(renderNotificationItem)}
                {additionalUnreadCount > 0 && (
                  <Box px={4} py={2} textAlign="center">
                    <Text fontSize="xs" color={theme.text.tertiary}>
                      {additionalUnreadCount === 1
                        ? "1 more unread notification"
                        : `${additionalUnreadCount} more unread notifications`}
                    </Text>
                  </Box>
                )}
              </>
            )}
          </Box>

          <Box flexShrink={0} pt={1}>
            <Divider borderColor={theme.borderGlass.default} mb={1} />
            <MenuItem
              px={4}
              py={2}
              color={theme.text.primary}
              bg="transparent"
              borderRadius="6px"
              w="calc(100% - 10px)"
              mx="5px"
              fontSize="sm"
              fontWeight="500"
              transition="all 0.15s ease-out"
              sx={{
                "@media (hover: hover)": {
                  "&:hover": {
                    bg: theme.accent.primaryBg,
                    color: theme.accent.primary,
                  },
                },
              }}
              onClick={handleShowAllNotifications}
            >
              <Text
                fontSize="sm"
                fontWeight="500"
                color={theme.accent.primary}
                w="100%"
                textAlign="center"
              >
                Show all notifications
              </Text>
            </MenuItem>
          </Box>
        </MenuList>
      </Portal>
    </Menu>
  );
};
